import { useCallback } from "react"
import { routes } from "common/navigationRoutes"
import { CONTENT_REFERENCE_TYPES } from "pages/games/common/constants"
import { selectProcessor } from "./useGamesDataProcessors"

const findFirstUnansweredIndex = (exercises, answers) =>
  exercises.findIndex(
    (content) =>
      !answers.some(
        (ans) =>
          ans.gameSessionContentId === content.gameSessionContentId ||
          ans.GameSessionContentId === content.gameSessionContentId
      )
  )

const findCurrentParticipant = (participants, userId) =>
  participants?.find((p) => p.userId === userId)

const useGameSession = ({
  gameSessionData,
  setGameSessionData,
  exercisesContent,
  exerciseContentIndex,
  setExercisesContent,
  setExerciseContentIndex,
  setIsGameFinished,
  setAnswers,
  setCountdownCompleted,
  setIsLoadingGameContent,
  setCurrentGame,
  formatOnlyAnswered,
  fetchGameById,
  getGameSessionById,
  setIndividualGameSession,
  retryIndividualGameSession,
  userData,
  selectedGroupData,
  navigate,
  showAlert,
  setSearchParams,
  searchParams,
  location,
}) => {
  const loadSession = useCallback(
    async (sessionId) => {
      try {
        const response = await getGameSessionById({ gameSessionId: sessionId })
        if (response !== null) {
          const session = response.gameSession || {}
          setGameSessionData(session)

          const gameDetails = await fetchGameById(session.gameId)
          setCurrentGame(gameDetails)

          const processed = selectProcessor(
            session?.gameId,
            session?.contents || session?.gameSessionContents
          )
          setExercisesContent(processed)

          const currentPlayerObj = findCurrentParticipant(
            session?.participants,
            userData?.userId
          )
          if (currentPlayerObj) {
            const answersFromPlayer = currentPlayerObj.answers || []
            const formatted = formatOnlyAnswered(answersFromPlayer, processed)
            setAnswers(formatted)
          } else {
            setAnswers([])
          }

          const currentGameSessionContentId =
            session?.currentGameSessionContentId
          const currentIndex = processed.findIndex(
            (content) =>
              content.gameSessionContentId === currentGameSessionContentId
          )
          const resolvedIndex = currentIndex >= 0 ? currentIndex : 0
          setExerciseContentIndex(resolvedIndex)

          return {
            gameSessionData: session,
            game: gameDetails,
            exerciseContentIndex: resolvedIndex,
          }
        } else {
          navigate(routes.NEW_GAMES)
        }
      } catch (error) {
        console.error("Error loading session:", error)
        navigate(routes.NEW_GAMES)
      }
    },
    [userData?.userId, formatOnlyAnswered]
  )

  const createGame = useCallback(
    async ({
      gameId,
      moduleId,
      sessionId = 0,
      metadata = "",
      gameMode = CONTENT_REFERENCE_TYPES.MODULE,
    }) => {
      const isResuming = sessionId > 0
      setCountdownCompleted(isResuming)
      setExerciseContentIndex(0)
      setIsGameFinished(false)
      setAnswers([])

      if (isResuming) {
        const response = await getGameSessionById({ gameSessionId: sessionId })
        const session = response?.gameSession || {}
        setGameSessionData(session)

        const processed = selectProcessor(session?.gameId, session?.contents)
        setExercisesContent(processed)

        const currentPlayerObj = findCurrentParticipant(
          session?.participants,
          userData?.userId
        )
        const answersFromPlayer = currentPlayerObj?.answers || []
        const formatted = formatOnlyAnswered(answersFromPlayer, processed)
        setAnswers(formatted)

        setCountdownCompleted(true)

        const firstUnansweredIndex = findFirstUnansweredIndex(
          processed,
          answersFromPlayer
        )
        if (firstUnansweredIndex >= 0) {
          setExerciseContentIndex(firstUnansweredIndex)
        } else if (processed.length > 0) {
          setIsGameFinished(true)
        }

        const currentId = session?.gameId
        if (currentId) {
          const gameDetails = await fetchGameById(currentId)
          if (gameDetails) {
            setCurrentGame(gameDetails)
          }
        }

        return {
          gameSessionData: session,
          exercisesContent: processed,
        }
      } else {
        const data = await setIndividualGameSession({
          gameId,
          moduleId,
          metadata,
          gameContentReferenceTypeId: gameMode,
          learningLanguageId: selectedGroupData?.learningLanguageId,
          ...(gameId === 3 && {
            gameContentReferenceTypeId: CONTENT_REFERENCE_TYPES.LEVEL,
          }),
        })
        if (!data?.contents || data?.contents?.length === 0) {
          showAlert("No content available")
          navigate(routes.NEW_GAMES)
          return null
        }
        setGameSessionData(data)
        const processed = selectProcessor(gameId, data?.contents)
        setExercisesContent(processed)
        return {
          gameSessionData: data,
          exercisesContent: processed,
        }
      }
    },
    [
      setIndividualGameSession,
      fetchGameById,
      selectedGroupData?.groupId,
      userData?.userId,
      formatOnlyAnswered,
    ]
  )

  const loadGameContent = useCallback(
    async ({ isRetry = false }) => {
      try {
        const queryParams = new URLSearchParams(location.search)
        const sessionId = queryParams.get("sessionId")
        const isResuming = sessionId !== null && sessionId !== "" && !isRetry

        if (!isResuming) {
          setExerciseContentIndex(0)
          setIsGameFinished(false)
          setAnswers([])
        }
        setIsLoadingGameContent(true)

        if (isRetry) {
          setCountdownCompleted(false)
          const data = await retryIndividualGameSession({
            gameSessionId: gameSessionData?.gameSessionId,
          })
          setGameSessionData(data)
          const processed = selectProcessor(data?.gameId, data?.contents)
          setExercisesContent(processed)
          setExerciseContentIndex(0)
          setIsLoadingGameContent(false)
          setSearchParams(
            {
              ...Object.fromEntries([...searchParams]),
              sessionId: data?.gameSessionId,
            },
            { replace: true }
          )
          return processed[0]
        }

        if (gameSessionData !== null) {
          const exercises = exercisesContent || []
          setIsLoadingGameContent(false)
          if (exercises?.length > 0) {
            return exercises[exerciseContentIndex]
          }
        } else {
          if (!sessionId) {
            setIsLoadingGameContent(false)
            navigate(routes.NEW_GAMES)
          } else {
            const response = await createGame({ sessionId })
            const exercises = response?.exercisesContent || []
            setIsLoadingGameContent(false)
            if (exercises?.length > 0) {
              const player = findCurrentParticipant(
                response?.gameSessionData?.participants,
                userData?.userId
              )
              const answersFromPlayer = player?.answers || []
              const firstUnansweredIndex = findFirstUnansweredIndex(
                exercises,
                answersFromPlayer
              )
              const startIndex =
                firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0
              return exercises[startIndex]
            }
            return {}
          }
        }
        setIsLoadingGameContent(false)
      } catch (error) {
        console.log("[loadGameContent error]", error)
        setIsLoadingGameContent(false)
      }
    },
    [
      gameSessionData,
      exercisesContent,
      searchParams,
      exerciseContentIndex,
      userData?.userId,
      createGame,
    ]
  )

  return {
    loadSession,
    createGame,
    loadGameContent,
  }
}

export default useGameSession
